/**
 * ItineraryCard — Phase 13A rich rendering.
 *
 * Renders BOTH the new Phase 13A schema (card_type="itinerary" with
 * `days[]`, `hotel`, `blocks`, `weather_summary`, `outbound_flight`,
 * `total_cost_estimate_inr`, etc.) AND the legacy seed-based schema
 * (`{city, days, plan[]}` with `stops`). Detection is keyed off the
 * presence of `days` as an array of dated day-blocks vs a number.
 */
import { useEffect, useMemo, useState } from "react";
import { BedDouble, ChevronDown, ChevronUp, Clock, MapPin, Moon, Plane, Share2, Sparkles, Sun, Sunrise, Wallet } from "lucide-react";
import { toast } from "sonner";
import FlightGroupCard from "@/components/cards/FlightGroupCard";
import CategoryGlyph from "@/components/CategoryGlyph";
import ItineraryMapView from "@/components/cards/ItineraryMapView";
import PlaceVideoLightbox from "@/components/PlaceVideoLightbox";
import { wholeRouteGoogleUrl, dayGoogleUrl, downloadKml } from "@/lib/mapLinks";

function TimeGlyph({ time }) {
    const t = String(time || "").toLowerCase();
    const Icon = t.startsWith("morn") ? Sunrise : (t.startsWith("even") || t.startsWith("night")) ? Moon : Sun;
    return <Icon size={11} strokeWidth={2.2} />;
}
import { formatPrice } from "@/lib/currency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDuration(mins) {
    const n = Number(mins) || 0;
    const h = Math.floor(n / 60);
    const m = n % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
}

function fmtINR(amount) {
    if (amount == null) return null;
    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `₹${amount}`;
    }
}

function fmtDateShort(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function fmtDateRange(start, end) {
    if (!start) return "";
    try {
        const s = new Date(start);
        const e = end ? new Date(end) : null;
        const opts = { month: "short", day: "numeric" };
        const sStr = s.toLocaleDateString(undefined, opts);
        if (!e) return sStr;
        const eStr = e.toLocaleDateString(undefined, { ...opts, year: "numeric" });
        return `${sStr} – ${eStr}`;
    } catch {
        return start;
    }
}

function resolveBookHref(book_url) {
    if (!book_url) return null;
    return book_url.startsWith("/api/r/")
        ? `${BACKEND_URL}${book_url}`
        : book_url;
}

function isPhase13ASchema(card) {
    // New schema carries `card_type === "itinerary"` and `days` is an
    // array of dated day objects. Legacy seed shape stores `days` as a
    // NUMBER (count) and the route plan under `plan[]`.
    if (!card) return false;
    if (card.card_type === "itinerary") return true;
    if (Array.isArray(card.days) && card.days.length > 0) {
        const first = card.days[0];
        return first && typeof first === "object" && ("blocks" in first || "hotel" in first);
    }
    return false;
}

const TIME_OF_DAY_ACCENT = {
    morning: "#F59E0B",
    afternoon: "#2563EB",
    evening: "#7C3AED",
    night: "#0A0A0A",
};

function timeAccent(time) {
    const key = (time || "").toLowerCase();
    return TIME_OF_DAY_ACCENT[key] || "#525252";
}

function providerLabel(provider) {
    const p = (provider || "").toLowerCase();
    if (p === "klook") return "Book on Klook";
    if (p === "viator") return "Book on Viator";
    if (p === "getyourguide") return "Book on GetYourGuide";
    return "Book this";
}

// ────────────────────────────────────────────────────────────────────
// Phase 13A — top-level entry
// ────────────────────────────────────────────────────────────────────
export default function ItineraryCard({ card, onSend }) {
    // Phase 17 — self-upgrading shell: poll the background job on
    // partial cards and swap in the full plan when it lands.
    const [liveCard, setLiveCard] = useState(card);
    useEffect(() => { setLiveCard(card); }, [card]);
    useEffect(() => {
        const c = liveCard;
        if (!c?.partial || !c?.job_id) return undefined;
        let alive = true;
        let tries = 0;
        const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
        const poll = async () => {
            tries += 1;
            try {
                const res = await fetch(`${BACKEND}/api/itinerary/jobs/${c.job_id}`);
                if (res.ok) {
                    const data = await res.json();
                    const full = (data?.cards || []).find(
                        (x) => x?.type === "itinerary" && !x?.partial,
                    );
                    if (!alive) return;
                    if (data?.status === "done" && full) { setLiveCard(full); return; }
                    if (data?.status === "failed") return;
                }
            } catch { /* transient — keep polling */ }
            if (alive && tries < 40) setTimeout(poll, 3500);
        };
        const t = setTimeout(poll, 3000);
        return () => { alive = false; clearTimeout(t); };
    }, [liveCard?.job_id, liveCard?.partial]);
    if (isPhase13ASchema(liveCard)) {
        return <Phase13AItineraryCard card={liveCard} onSend={onSend} />;
    }
    return <LegacyItineraryCard card={liveCard} />;
}

function Phase13AItineraryCard({ card, onSend }) {
    const [activeDay, setActiveDay] = useState(0);
    const [showMap, setShowMap] = useState(false);
    const [videoPlace, setVideoPlace] = useState(null);
    const mappable = (card?.days || []).filter((d) => d?.city_coords && Number.isFinite(d.city_coords.lat)).length >= 2;
    const {
        title,
        destination_summary,
        must_try_food = [],
        duration_days,
        travelers,
        start_date,
        end_date,
        vibe,
        total_cost_estimate_inr,
        weather_summary,
        outbound_flight,
        return_flight,
        days = [],
        share_url,
        modify_cta,
        whole_trip_package_alternatives,
    } = card;

    const vibeLabel = useMemo(() => {
        if (!vibe) return null;
        if (Array.isArray(vibe)) return vibe.slice(0, 3).join(" · ");
        return String(vibe);
    }, [vibe]);

    const handleShare = async () => {
        const path = share_url || "";
        const absolute = path.startsWith("http")
            ? path
            : typeof window !== "undefined"
            ? `${window.location.origin}${path}`
            : path;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(absolute);
                toast.success("Link copied", { description: absolute });
            } else {
                toast.success("Share link", { description: absolute });
            }
        } catch {
            toast.error("Could not copy link");
        }
    };

    const handleModify = () => {
        const seed = `Modify this itinerary: `;
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("tc:prefill-composer", { detail: { text: seed } }),
            );
            toast.info("Tell me what to change", {
                description: "Composer prefilled — edit and hit send.",
            });
            return;
        }
        if (typeof onSend === "function") onSend(seed);
    };

    const costLine = (() => {
        const c = total_cost_estimate_inr;
        if (!c || (c.low == null && c.high == null)) return null;
        if (c.low != null && c.high != null && c.low !== c.high) {
            return `${fmtINR(c.low)} – ${fmtINR(c.high)} estimated total`;
        }
        return `${fmtINR(c.low ?? c.high)} estimated total`;
    })();

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5 sm:p-6"
            data-testid="itinerary-card"
            data-card-type="itinerary"
        >
            {/* Orange dot — itinerary accent */}
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#F97316] ring-4 ring-[#F97316]/15"
                data-testid="card-color-dot"
                data-color="orange"
            />

            <header className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                        Smart Itinerary
                    </div>
                    <h2
                        className="mt-1 truncate text-[20px] font-bold tracking-tight text-[#0A0A0A] sm:text-[22px]"
                        data-testid="itinerary-title"
                    >
                        {title || `${duration_days || days.length}-day plan`}
                    </h2>
                    {destination_summary && (
                        <div
                            className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[#525252]"
                            data-testid="itinerary-destination-summary"
                        >
                            <MapPin size={12} strokeWidth={2} className="text-[#A3A3A3]" />
                            <span>{destination_summary}</span>
                            {travelers ? (
                                <>
                                    <span className="text-[#D4D4D4]">·</span>
                                    <span>
                                        {travelers} {travelers === 1 ? "traveler" : "travelers"}
                                    </span>
                                </>
                            ) : null}
                        </div>
                    )}
                    {(start_date || end_date) && (
                        <div className="mt-0.5 text-[12px] text-[#737373]">
                            {fmtDateRange(start_date, end_date)}
                        </div>
                    )}
                    {vibeLabel && (
                        <div className="mt-1 text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                            {vibeLabel}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    {share_url && (
                        <button
                            type="button"
                            onClick={handleShare}
                            data-testid="itinerary-share-btn"
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                        >
                            <Share2 size={12} strokeWidth={2} />
                            Share
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleModify}
                        data-testid="itinerary-modify-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-[#262626]"
                    >
                        <Sparkles size={12} strokeWidth={2} />
                        {modify_cta || "Modify"}
                    </button>
                </div>
            </header>

            {(costLine || weather_summary) && (
                <div className="mt-4 grid gap-2 border-t border-[#E5E5E5] pt-4 sm:grid-cols-2">
                    {costLine && (
                        <div
                            className="flex items-center gap-2 text-[13px] text-[#0A0A0A]"
                            data-testid="itinerary-cost-line"
                        >
                            <Wallet size={14} strokeWidth={2} className="text-[#525252]" />
                            <span className="font-semibold">{costLine}</span>
                        </div>
                    )}
                    {weather_summary && (
                        <div
                            className="flex items-center gap-2 text-[13px] text-[#525252]"
                            data-testid="itinerary-weather-summary"
                        >
                            <Sun size={14} strokeWidth={2} className="text-[#F59E0B]" />
                            <span>{weather_summary}</span>
                        </div>
                    )}
                </div>
            )}

            {Array.isArray(whole_trip_package_alternatives) &&
                whole_trip_package_alternatives.length > 0 && (
                    <div
                        className="mt-4 border-t border-[#E5E5E5] pt-3"
                        data-testid="itinerary-whole-trip-alternatives"
                    >
                        <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                            Or book the whole trip via
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {whole_trip_package_alternatives.map((alt, i) => (
                                <a
                                    key={alt.provider || i}
                                    href={alt.book_url}
                                    target="_blank"
                                    rel="noopener noreferrer sponsored"
                                    data-testid={`whole-trip-alt-${alt.provider}`}
                                    data-provider={alt.provider}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5] hover:border-[#0A0A0A]"
                                >
                                    {alt.label || alt.provider}
                                    <span aria-hidden className="text-[#737373]">↗</span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

            {outbound_flight && (
                <CollapsibleFlight
                    flight={outbound_flight}
                    label="Outbound flight"
                    testid="itinerary-outbound-flight"
                    initiallyOpen
                />
            )}

            {mappable && (
                <button
                    type="button"
                    onClick={() => setShowMap((v) => !v)}
                    data-testid="itinerary-map-toggle"
                    className={`mt-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-medium transition ${showMap ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:border-[#0A0A0A]"}`}
                >
                    🗺 {showMap ? "Hide map" : "Map view"}
                </button>
            )}
            {mappable && showMap && (
                <ItineraryMapView card={card} onClose={() => setShowMap(false)} />
            )}
            {mappable && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="itinerary-handoff-row">
                    {wholeRouteGoogleUrl(card) && (
                        <a href={wholeRouteGoogleUrl(card)} target="_blank" rel="noopener noreferrer"
                           data-testid="itinerary-open-gmaps"
                           className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]">
                            ↗ Open route in Google Maps
                        </a>
                    )}
                    <button type="button" onClick={() => downloadKml(card)}
                            data-testid="itinerary-export-kml"
                            className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]">
                        ⬇ Export for offline maps (KML)
                    </button>
                </div>
            )}

            <div className="mt-5" data-testid="itinerary-days">
                {days.map((day, idx) => (
                    <div
                        key={day.day || day.date || idx}
                        className="flex items-stretch gap-2"
                        data-testid={`itinerary-rail-row-${idx + 1}`}
                    >
                        {/* Phase 22.2 — journey rail: line + day nodes. */}
                        <div className="flex w-[22px] flex-col items-center">
                            <div className={`h-2.5 w-0.5 ${idx === 0 ? "bg-transparent" : "bg-[#E5E5E5]"}`} />
                            <div className={`flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] transition-colors ${idx === activeDay ? "border-[#0A0A0A] bg-[#0A0A0A]" : "border-[#D4D4D4] bg-white"}`}>
                                <span className={`text-[10px] font-semibold ${idx === activeDay ? "text-white" : "text-[#737373]"}`}>
                                    {day.day || idx + 1}
                                </span>
                            </div>
                            <div className={`w-0.5 flex-1 ${idx === days.length - 1 ? "bg-transparent" : "bg-[#E5E5E5]"}`} />
                        </div>
                        <div className="min-w-0 flex-1 pb-3">
                            <DayBlock
                                day={day}
                                initiallyOpen={idx === 0}
                                onOpenChange={(o) => o && setActiveDay(idx)}
                                onOpenVideo={setVideoPlace}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {return_flight && (
                <CollapsibleFlight
                    flight={return_flight}
                    label="Return flight"
                    testid="itinerary-return-flight"
                />
            )}

            {/* Phase 16.1 — local specials from `must_try_food`
                (previously shipped by the backend but never rendered).
                Tappable → asks the agent where to eat it. */}
            {Array.isArray(must_try_food) && must_try_food.length > 0 && (
                <div className="mt-4 space-y-1.5" data-testid="itinerary-food-row">
                    <div className="text-[10px] font-semibold tracking-[0.18em] text-[#A3A3A3]">🍜 MUST-TRY LOCAL FOOD</div>
                    <div className="flex flex-wrap gap-1.5">
                        {must_try_food.slice(0, 6).map((dish) => (
                            <button
                                key={dish}
                                type="button"
                                onClick={() => onSend?.(`Where can I try ${dish} in ${destination_summary}?`)}
                                data-testid={`itinerary-food-${String(dish).replace(/\s+/g, "-").toLowerCase()}`}
                                className="rounded-full border border-[#FDE68A] bg-[#FFFBEB] px-3 py-1 text-[11.5px] font-medium text-[#92400E] transition hover:border-[#92400E]"
                            >
                                {dish}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {videoPlace && (
                <PlaceVideoLightbox place={videoPlace} onClose={() => setVideoPlace(null)} />
            )}

            {/* Phase 26 — another-option row: shuffle / add cities / offbeat. */}
            {typeof onSend === "function" && (
                <div className="mt-4" data-testid="itinerary-another-row">
                    <p className="text-[12px] font-medium text-[#525252]">Need another itinerary option?</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <button type="button" onClick={() => onSend("Give me a different itinerary option for this trip — shuffle the cities and places, same dates and budget")}
                                data-testid="itinerary-shuffle"
                                className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]">
                            🔀 Shuffle plan
                        </button>
                        <button type="button" onClick={() => onSend("Add one more city to this itinerary and rebalance the days")}
                                data-testid="itinerary-add-city"
                                className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]">
                            ➕ Add a city
                        </button>
                        <button type="button" onClick={() => onSend("Rebuild this itinerary offbeat — hidden gems, local favourites, and fewer touristy spots")}
                                data-testid="itinerary-offbeat"
                                className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]">
                            🧭 Make it offbeat
                        </button>
                    </div>
                </div>
            )}

            {/* Phase 22.1 — plan-aware follow-up question with tappable reshapes. */}
            {typeof onSend === "function" && !!card?.follow_up?.question &&
             Array.isArray(card?.follow_up?.options) && card.follow_up.options.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="itinerary-followup-row">
                    <p className="text-[12.5px] font-medium italic leading-relaxed text-[#0A0A0A]">
                        {String(card.follow_up.question)}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {card.follow_up.options.slice(0, 3).map((o, i) => (
                            !!o?.label && !!o?.query ? (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => onSend(String(o.query))}
                                    data-testid={`itinerary-followup-${i}`}
                                    className="rounded-full border border-[#DDD6FE] bg-[#F5F3FF] px-3 py-1.5 text-[11.5px] font-medium text-[#5B21B6] transition hover:border-[#5B21B6]"
                                >
                                    {String(o.label)}
                                </button>
                            ) : null
                        ))}
                    </div>
                </div>
            )}

            {/* Phase 16 — editable-itinerary chips. Each re-invokes the
                agent through chat WITH conversation context, so the
                itinerary evolves instead of restarting. */}
            {typeof onSend === "function" && (
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid="itinerary-refine-row">
                    {[
                        ["cheaper", "💸 Make it cheaper", "Make this itinerary cheaper — swap in budget options"],
                        ["food", "🍜 More food spots", "Add more local food experiences to this itinerary"],
                        ["slower", "🌿 Slower pace", "Slow the pace of this itinerary — fewer stops per day"],
                        ["swap", "🔁 Swap hotels", "Suggest alternative hotels for this itinerary"],
                        ["extend", "➕ Add a day", "Extend this itinerary by one more day"],
                    ].map(([key, label, query]) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onSend(query)}
                            data-testid={`itinerary-refine-${key}`}
                            className="rounded-full border border-[#E5E5E5] bg-white px-3 py-1 text-[11.5px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </article>
    );
}

// ────────────────────────────────────────────────────────────────────
// Day block (Phase 13A)
// ────────────────────────────────────────────────────────────────────
function DayBlock({ day, initiallyOpen, onOpenChange, onOpenVideo }) {
    const [open, setOpen] = useState(!!initiallyOpen);
    const {
        day: dayNum,
        date,
        city,
        title,
        hotel,
        blocks = [],
        daily_cost_estimate_inr,
    } = day;

    const headlineCity = (() => {
        if (!city) return title || "";
        if (typeof city === "string") return city;
        return city.name || city.city || "";
    })();

    return (
        <div
            className="overflow-hidden rounded-xl border border-[#E5E5E5] bg-white"
            data-testid={`itinerary-day-${dayNum || ""}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => { onOpenChange?.(!v); return !v; })}
                className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-[#FAFAFA]"
                data-testid={`itinerary-day-toggle-${dayNum || ""}`}
                aria-expanded={open}
            >
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                        Day {dayNum}
                        {headlineCity ? <span>{"  ·  "}{headlineCity}</span> : null}
                        {date && <span>{"  ·  "}{fmtDateShort(date)}</span>}
                    </div>
                    <div className="mt-0.5 truncate text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                        {(day?.title && String(day.title)) || headlineCity || `Day ${dayNum}`}
                        {headlineCity && typeof onOpenVideo === "function" && (
                            <sup>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onOpenVideo(headlineCity); }}
                                    data-testid={`itinerary-day-video-${dayNum || ""}`}
                                    title={`Watch ${headlineCity} videos`}
                                    className="ml-1 align-super text-[9px] text-[#DC2626] transition hover:scale-110"
                                >▶</button>
                            </sup>
                        )}
                    </div>
                    {!!day?.story && (
                        <div className="mt-0.5 truncate text-[12px] italic text-[#737373]"
                             data-testid={`itinerary-day-story-${dayNum || ""}`}>
                            {String(day.story)}
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                    {blocks.length > 0 && (
                        <span className="hidden text-[11px] text-[#737373] sm:inline">
                            {blocks.length} {blocks.length === 1 ? "block" : "blocks"}
                        </span>
                    )}
                    {open ? (
                        <ChevronUp size={15} strokeWidth={2} className="text-[#737373]" />
                    ) : (
                        <ChevronDown size={15} strokeWidth={2} className="text-[#737373]" />
                    )}
                </div>
            </button>

            {open && (
                <div
                    className="border-t border-[#E5E5E5] px-4 py-4"
                    data-testid={`itinerary-day-body-${dayNum || ""}`}
                >
                    {hotel && <HotelRow hotel={hotel} />}
                    {blocks.length > 0 && (
                        <ol className="mt-3 space-y-3">
                            {dayGoogleUrl(day) && (
                                <a href={dayGoogleUrl(day)} target="_blank" rel="noopener noreferrer"
                                   data-testid={`itinerary-day-nav-${dayNum || ""}`}
                                   className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#2563EB] hover:underline">
                                    🧭 Navigate this day
                                </a>
                            )}
                            {blocks.map((b, i) => (
                                <BlockRow key={i} block={b} />
                            ))}
                        </ol>
                    )}
                    {daily_cost_estimate_inr != null && (
                        <div
                            className="mt-3 border-t border-[#F5F5F5] pt-2 text-[12px] text-[#525252]"
                            data-testid={`itinerary-day-cost-${dayNum || ""}`}
                        >
                            Daily est: {fmtINR(daily_cost_estimate_inr)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function HotelRow({ hotel }) {
    if (!hotel) return null;
    const { name, city, price, provider, book_url } = hotel;
    const href = resolveBookHref(book_url);
    // Phase 14.8.3 — `hotel.price` is canonically `{amount, currency}`
    // (see /app/backend/agents/itinerary_agent.py:303 → `price_per_night`).
    // Rendering the raw object into JSX crashes React with error #31
    // ("Objects are not valid as a React child"); route through
    // `formatPrice` which now safely handles object / primitive /
    // string / null inputs.
    const priceText = formatPrice(price);
    return (
        <div
            className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2.5"
            data-testid="itinerary-hotel-row"
        >
            <div className="flex min-w-0 items-center gap-2.5">
                <span
                    aria-hidden
                    className="inline-block h-2 w-2 shrink-0 rounded-full bg-[#16A34A]"
                />
                <BedDouble size={14} strokeWidth={2} className="shrink-0 text-[#525252]" />
                <div className="min-w-0">
                    <div className="truncate text-[13px] font-medium text-[#0A0A0A]">
                        {name || "Hotel"}
                    </div>
                    {(city || provider) && (
                        <div className="truncate text-[11px] text-[#737373]">
                            {[city, provider].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                {priceText && (
                    <span
                        className="text-[12px] font-medium text-[#0A0A0A]"
                        data-testid="itinerary-hotel-price"
                    >
                        {priceText}
                    </span>
                )}
                {href && (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid="itinerary-hotel-book-link"
                        className="inline-flex items-center rounded-md bg-[#0A0A0A] px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-[#262626]"
                    >
                        Book Now
                    </a>
                )}
            </div>
        </div>
    );
}

function BlockRow({ block }) {
    const {
        time,
        activity,
        category,
        duration_min,
        transit_from_previous,
        notes,
        book_url,
        book_provider,
    } = block || {};
    const accent = timeAccent(time);
    const href = resolveBookHref(book_url);
    return (
        <li
            className="flex items-start gap-3"
            data-testid="itinerary-block"
            data-time={time || undefined}
            data-provider={book_provider || undefined}
        >
            <span
                aria-hidden
                className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: accent }}
            />
            <div className="min-w-0 flex-1">
                {transit_from_previous && (
                    <div
                        className="mb-1 text-[11px] text-[#A3A3A3]"
                        data-testid="itinerary-block-transit"
                    >
                        → {transit_from_previous}
                    </div>
                )}
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    {time && (
                        <span className="inline-flex items-center gap-1" style={{ color: accent }}>
                            <TimeGlyph time={time} />
                            <span className="text-[10px] font-semibold uppercase tracking-wider">
                                {time}
                            </span>
                        </span>
                    )}
                    <span className="text-[14px] font-medium text-[#0A0A0A]">
                        {activity || "Activity"}
                    </span>
                    {duration_min ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#737373]">
                            <Clock size={10} strokeWidth={2} />
                            {fmtDuration(duration_min)}
                        </span>
                    ) : null}
                    {category && (
                        <span className="inline-flex items-center gap-1" data-testid="itinerary-block-category">
                            <CategoryGlyph name={category} size={11} />
                        <span className="rounded-full bg-[#F5F5F5] px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#525252]">
                            {category}
                        </span>
                                            </span>
                    )}
                </div>
                {notes && (
                    <p className="mt-0.5 text-[12px] leading-relaxed text-[#737373]">
                        {notes}
                    </p>
                )}
                {href && (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid="itinerary-block-book-link"
                        data-provider={book_provider}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium text-[#0A0A0A] underline decoration-dotted underline-offset-[3px] transition hover:text-[#F97316]"
                    >
                        <span
                            aria-hidden
                            className="inline-block h-1.5 w-1.5 rounded-full"
                            style={{
                                background:
                                    (book_provider || "").toLowerCase() === "viator"
                                        ? "#1E40AF"
                                        : "#F97316",
                            }}
                        />
                        {providerLabel(book_provider)}
                    </a>
                )}
            </div>
        </li>
    );
}

function CollapsibleFlight({ flight, label, testid, initiallyOpen = false }) {
    const [open, setOpen] = useState(!!initiallyOpen);
    return (
        <div
            className="mt-4 overflow-hidden rounded-xl border border-[#E5E5E5] bg-white"
            data-testid={testid}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-[#FAFAFA]"
                aria-expanded={open}
                data-testid={`${testid}-toggle`}
            >
                <div className="flex items-center gap-2">
                    <Plane size={14} strokeWidth={2} className="text-[#2563EB]" />
                    <span className="text-[13px] font-medium text-[#0A0A0A]">{label}</span>
                </div>
                {open ? (
                    <ChevronUp size={15} strokeWidth={2} className="text-[#737373]" />
                ) : (
                    <ChevronDown size={15} strokeWidth={2} className="text-[#737373]" />
                )}
            </button>
            {open && (
                <div className="border-t border-[#E5E5E5] p-3">
                    <FlightGroupCard card={flight} />
                </div>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Legacy renderer (seed-based plan/stops shape) — preserved verbatim.
// ────────────────────────────────────────────────────────────────────
function LegacyItineraryCard({ card }) {
    const { city, days, plan = [] } = card;
    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="itinerary-card"
            data-card-type="itinerary-legacy"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#F97316] ring-4 ring-[#F97316]/15"
                data-testid="card-color-dot"
                data-color="orange"
            />
            <div>
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                    Smart Itinerary
                </div>
                <h2 className="mt-1 text-[20px] font-semibold tracking-tight">
                    {days}-day plan for {city}
                </h2>
            </div>
            <div className="mt-5 space-y-3">
                {plan.map((day) => (
                    <LegacyDayBlock
                        key={day.day}
                        day={day}
                        initiallyOpen={day.day === 1}
                    />
                ))}
            </div>
        </article>
    );
}

function LegacyDayBlock({ day, initiallyOpen }) {
    const [open, setOpen] = useState(!!initiallyOpen);
    return (
        <div
            className="rounded-xl border border-[#E5E5E5] bg-white"
            data-testid={`itinerary-day-${day.day}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                data-testid={`itinerary-day-toggle-${day.day}`}
            >
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                        Day {day.day}
                    </div>
                    <div className="text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                        {day.title}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className="hidden text-[11px] text-[#737373] sm:inline">
                        {day.stops?.length} stops · {fmtDuration(day.total_minutes)}
                    </span>
                    {open ? (
                        <ChevronUp size={15} strokeWidth={2} className="text-[#737373]" />
                    ) : (
                        <ChevronDown size={15} strokeWidth={2} className="text-[#737373]" />
                    )}
                </div>
            </button>
            {open && (
                <ol className="space-y-2 border-t border-[#E5E5E5] px-4 py-3">
                    {(day.stops || []).map((s, i) => (
                        <li
                            key={s.id}
                            className="text-[13px] leading-relaxed text-[#0A0A0A]"
                            data-testid={`itinerary-stop-${s.id}`}
                        >
                            {s.from_prev && (
                                <div
                                    className="ml-7 flex items-center gap-1.5 py-1 text-[11px] text-[#A3A3A3]"
                                    data-testid={`itinerary-transit-${s.id}`}
                                >
                                    <span className="inline-block h-3 w-px bg-[#E5E5E5]" />
                                    → {s.from_prev.distance_km} km · {s.from_prev.transit_minutes} min
                                </div>
                            )}
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#0A0A0A] text-[11px] font-semibold text-white">
                                    {i + 1}
                                </span>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">{s.name}</span>
                                        <span className="inline-flex items-center gap-1 text-[11px] text-[#737373]">
                                            <Clock size={10} strokeWidth={2} />
                                            {fmtDuration(s.visit_minutes)}
                                        </span>
                                    </div>
                                    {s.description && (
                                        <p className="mt-0.5 text-[12px] text-[#737373]">
                                            {s.description}
                                        </p>
                                    )}
                                    {Array.isArray(s.bookable_experiences) &&
                                        s.bookable_experiences.length > 0 && (
                                            <div
                                                className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1"
                                                data-testid={`itinerary-bookable-${s.id}`}
                                            >
                                                {s.bookable_experiences.map((b, j) => {
                                                    const target = resolveBookHref(b.book_url);
                                                    const label = b.source_label
                                                        ? `Book on ${b.source_label}`
                                                        : "Book this";
                                                    return (
                                                        <a
                                                            key={j}
                                                            href={target}
                                                            target="_blank"
                                                            rel="noopener noreferrer sponsored"
                                                            data-testid={`itinerary-bookable-link-${s.id}-${j}`}
                                                            data-provider={b.provider}
                                                            title={b.title}
                                                            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[#0A0A0A] underline decoration-dotted underline-offset-[3px] transition hover:text-[#F59E0B]"
                                                        >
                                                            <span
                                                                aria-hidden
                                                                className="inline-block h-1.5 w-1.5 rounded-full"
                                                                style={{ background: "#F59E0B" }}
                                                            />
                                                            {label}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
